import { useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * „Einfache E-Mail" — Kontaktanfrage an den Verkäufer, die direkt im
 * Händler-Dashboard (Nachrichten / SmartInbox) landet, statt das lokale
 * E-Mail-Programm zu öffnen. Für ausgeloggte Besucher wird (analog zum
 * Gast-Preisvorschlag) ein passwortloses Gast-Konto angelegt und der Besucher
 * automatisch eingeloggt — so kann er die Antwort des Händlers im eigenen
 * Postfach verfolgen.
 */
export default function SellerEmailButton({ listing, extraText = "" }) {
    const { user, setSession } = useAuth();
    const isLoggedIn = !!(user && user !== false);

    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [guestFirst, setGuestFirst] = useState("");
    const [guestLast, setGuestLast] = useState("");
    const [guestEmail, setGuestEmail] = useState("");
    const [guestPhone, setGuestPhone] = useState("");
    const [existingAccount, setExistingAccount] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const openDialog = () => {
        setError("");
        setExistingAccount(false);
        let base = message.trim()
            ? message
            : `Guten Tag,\n\nich interessiere mich für Ihr Inserat „${listing.title}". Können Sie mir bitte folgende Frage beantworten:\n\n`;
        // Jun-2026 (User-Wunsch) — gewähltes Zubehör aus dem Schaufenster anhängen.
        if (extraText && !base.includes(extraText.trim())) base += extraText;
        setMessage(base);
        setOpen(true);
    };

    const submit = async () => {
        setError("");
        const text = message.trim();
        if (text.length < 5) {
            setError("Bitte formulieren Sie Ihre Nachricht (mind. 5 Zeichen).");
            return;
        }
        const wasGuest = !isLoggedIn;
        const guestEmailUsed = guestEmail.trim();
        if (!isLoggedIn) {
            const first = guestFirst.trim();
            const last = guestLast.trim();
            const name = `${first} ${last}`.trim();
            const email = guestEmail.trim();
            const phone = guestPhone.trim();
            if (first.length < 2) { setError("Bitte Ihren Vornamen eingeben"); return; }
            if (last.length < 2) { setError("Bitte Ihren Nachnamen eingeben"); return; }
            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
                setError("Bitte eine gültige E-Mail-Adresse eingeben");
                return;
            }
            if (phone.replace(/[^0-9]/g, "").length < 6) { setError("Bitte eine gültige Handynummer eingeben"); return; }
            setSubmitting(true);
            try {
                const { data } = await api.post("/auth/guest", { name, email, phone: phone || null });
                if (data.existing) {
                    setSubmitting(false);
                    setExistingAccount(true);
                    setError("Für diese E-Mail existiert bereits ein Konto. Bitte einloggen für vollen Zugriff.");
                    return;
                }
                setSession(data.user, data.token);
            } catch (e) {
                setSubmitting(false);
                setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                return;
            }
        } else {
            setSubmitting(true);
        }
        try {
            await api.post(`/listings/${listing.id}/contact-seller`, { message: text });
            toast.success("Anfrage an den Verkäufer gesendet — die Antwort finden Sie in Ihrem Postfach.");
            if (wasGuest) {
                const { promptSecureAccount } = await import("../../lib/secureAccount");
                promptSecureAccount(guestEmailUsed);
            }
            setOpen(false);
            setMessage("");
            setGuestFirst("");
            setGuestLast("");
            setGuestEmail("");
            setGuestPhone("");
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || "Senden fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openDialog}
                className="w-full justify-start text-xs h-9 rounded-sm"
                data-testid="quick-action-email"
            >
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Einfache E-Mail
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="rounded-sm" data-testid="seller-email-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
                            <Mail className="w-6 h-6 text-[#FFCC00]" /> E-Mail an den Verkäufer
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            Ihre Anfrage landet direkt im Postfach des Verkäufers. Sie erhalten die Antwort in Ihrem TwoWheels-Postfach.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        {!isLoggedIn && (
                            <div className="space-y-3 pb-4 border-b border-gray-100" data-testid="seller-email-guest-fields">
                                <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-sm p-2.5 text-xs text-[#92400E]">
                                    Kein Konto nötig — wir legen automatisch eines für Sie an, damit Sie die Antwort verfolgen können.
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Vorname</label>
                                        <Input
                                            value={guestFirst}
                                            onChange={(e) => setGuestFirst(e.target.value)}
                                            placeholder="Max"
                                            className="rounded-sm h-11"
                                            data-testid="seller-email-first-input"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Nachname</label>
                                        <Input
                                            value={guestLast}
                                            onChange={(e) => setGuestLast(e.target.value)}
                                            placeholder="Mustermann"
                                            className="rounded-sm h-11"
                                            data-testid="seller-email-last-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">E-Mail</label>
                                    <Input
                                        type="email"
                                        value={guestEmail}
                                        onChange={(e) => { setGuestEmail(e.target.value); setExistingAccount(false); }}
                                        placeholder="ihre@email.de"
                                        className="rounded-sm h-11"
                                        data-testid="seller-email-email-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Handynummer</label>
                                    <Input
                                        type="tel"
                                        value={guestPhone}
                                        onChange={(e) => setGuestPhone(e.target.value)}
                                        placeholder="z.B. 0151 23456789"
                                        className="rounded-sm h-11"
                                        data-testid="seller-email-phone-input"
                                    />
                                </div>
                                {existingAccount && (
                                    <a
                                        href={`/login?email=${encodeURIComponent(guestEmail.trim())}`}
                                        className="inline-flex items-center justify-center w-full h-10 rounded-sm border border-[#111827] text-sm font-bold text-[#111827] hover:bg-gray-50"
                                        data-testid="seller-email-existing-login-link"
                                    >
                                        Jetzt einloggen
                                    </a>
                                )}
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Ihre Nachricht</label>
                            <Textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="rounded-sm min-h-[140px]"
                                maxLength={2000}
                                data-testid="seller-email-message-input"
                            />
                        </div>
                        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm" data-testid="seller-email-error">{error}</div>}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Abbrechen</Button>
                        <Button
                            onClick={submit}
                            disabled={submitting}
                            className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                            data-testid="seller-email-submit-btn"
                        >
                            <Send className="w-4 h-4 mr-1" /> {isLoggedIn ? "Anfrage senden" : "Konto erstellen & senden"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
